import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { V } from '../../utils/theme';
import { appName } from '../../utils/brand';
import { Icons } from '../../components/Icons';
import { Btn } from '../../components/ui';

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding + landing presentational building blocks.
// Pure UI — no network, no storage. Everything reads theme tokens from V so it
// renders correctly in both dark and light mode.
// ─────────────────────────────────────────────────────────────────────────────

import { gradient, track, LogoMark, Wordmark, Eyebrow, IconTile, ProgressSteps, CodeInput, Notice, Callout, LinkBtn, NumberedStep } from '../../components/kit';

// Re-export the shared kit so the onboarding flow keeps a single import
export { LogoMark, Wordmark, Eyebrow, IconTile, ProgressSteps, CodeInput, Notice, Callout, LinkBtn };

// Step header — icon tile, title, subtitle
export const StepHeader = ({ icon, color, title, subtitle }) => (
  <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 22 }}>
    {icon && <IconTile icon={icon} color={color} />}
    <div style={{ minWidth: 0 }}>
      <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15, color: V.text }}>{title}</h2>
      {subtitle && <div style={{ fontSize: 14, color: V.text2, marginTop: 6, lineHeight: 1.5 }}>{subtitle}</div>}
    </div>
  </div>
);

// Section divider inside a long step (e.g. the profile form)
export const SectionLabel = ({ title, hint, style = {} }) => (
  <div style={{ margin: "22px 0 12px", ...style }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Eyebrow color={V.text2}>{title}</Eyebrow>
      <div style={{ flex: 1, height: 1, background: V.divider }} />
    </div>
    {hint && <div style={{ fontSize: 12, color: V.text3, marginTop: 4 }}>{hint}</div>}
  </div>
);

// Selectable card — used for units, splits, goals. Ring + check badge when selected.
export const OptionCard = ({ selected, onClick, icon, color = V.accent, title, desc, children, center, style = {}, ariaLabel }) => (
  <button type="button" onClick={onClick} aria-pressed={!!selected} aria-label={ariaLabel} className="hover-lift" style={{
    display: "flex", alignItems: center ? "center" : "flex-start", flexDirection: center ? "column" : "row",
    gap: center ? 8 : 14, width: "100%", padding: center ? "20px 14px" : "14px 16px", textAlign: center ? "center" : "left",
    borderRadius: 16, cursor: "pointer", WebkitTapHighlightColor: "transparent", fontFamily: V.font,
    position: "relative", overflow: "hidden",
    background: selected ? V.cardGlow : V.cardGrad,
    border: `1.5px solid ${selected ? V.accent : V.cardBorder}`,
    boxShadow: selected ? `0 0 0 3px ${V.accent}22, ${V.shadow}` : V.shadow,
    transition: "border-color .15s, box-shadow .2s, background .2s, transform .12s",
    ...style,
  }}>
    {icon && <IconTile icon={icon} color={selected ? color : V.text2} size={center ? 48 : 42} />}
    <div style={{ flex: 1, minWidth: 0, paddingRight: center ? 0 : 22 }}>
      {title && <div style={{ fontSize: 15, fontWeight: 700, color: V.text, lineHeight: 1.25 }}>{title}</div>}
      {desc && <div style={{ fontSize: 12, color: V.text3, marginTop: 3, lineHeight: 1.45 }}>{desc}</div>}
      {children}
    </div>
    <div aria-hidden="true" style={{
      position: "absolute", top: 10, right: 10, width: 20, height: 20, borderRadius: 10,
      background: selected ? V.accent : "transparent", border: `1.5px solid ${selected ? V.accent : V.cardBorder}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      transform: selected ? "scale(1)" : "scale(.8)", opacity: selected ? 1 : 0.6, transition: "all .18s",
    }}>
      {selected && Icons.check({ size: 12, color: V.mode === "dark" ? V.bg : "#fff", strokeWidth: 3.2 })}
    </div>
  </button>
);

// Pill-style segmented control (e.g. Male / Female, weekly availability)
export const Segmented = ({ options, value, onChange, small }) => (
  <div role="radiogroup" style={{ display: "flex", gap: 4, padding: 4, borderRadius: 12, background: V.inputBg, border: `1px solid ${V.cardBorder}` }}>
    {options.map(o => {
      const opt = typeof o === "string" ? { value: o, label: o } : o;
      const on = value === opt.value;
      return (
        <button key={opt.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(opt.value)} style={{
          flex: 1, padding: small ? "8px 6px" : "10px 8px", borderRadius: 9, border: "none", cursor: "pointer",
          fontFamily: V.font, fontSize: small ? 12 : 13, fontWeight: 700, minHeight: small ? 34 : 40,
          background: on ? (V.mode === "dark" ? "rgba(255,255,255,0.09)" : "#fff") : "transparent",
          color: on ? V.accent : V.text3, boxShadow: on ? V.shadow : "none",
          WebkitTapHighlightColor: "transparent", transition: "all .15s",
        }}>{opt.label}</button>
      );
    })}
  </div>
);

// Chip-style multi option row (wraps) — single select
export const ChipRow = ({ options, value, onChange }) => (
  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
    {options.map(o => {
      const on = value === o;
      return (
        <button key={o} type="button" aria-pressed={on} onClick={() => onChange(o)} style={{
          padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontFamily: V.font, fontSize: 12, fontWeight: 700,
          border: `1.5px solid ${on ? V.accent : V.cardBorder}`, background: on ? `${V.accent}14` : V.card,
          color: on ? V.accent : V.text3, WebkitTapHighlightColor: "transparent", transition: "all .15s", minHeight: 36,
        }}>{o}</button>
      );
    })}
  </div>
);

// Multi-select chip row with an exclusive "none" option
export const MultiChipRow = ({ options, values = [], onChange, noneLabel }) => {
  const noneOn = values.length === 0;
  const chip = (label, on, onClick) => (
    <button key={label} type="button" aria-pressed={on} onClick={onClick} style={{
      padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontFamily: V.font, fontSize: 12, fontWeight: 700,
      border: `1.5px solid ${on ? V.accent : V.cardBorder}`, background: on ? `${V.accent}14` : V.card,
      color: on ? V.accent : V.text3, WebkitTapHighlightColor: "transparent", transition: "all .15s", minHeight: 36,
    }}>{label}</button>
  );
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {noneLabel && chip(noneLabel, noneOn, () => onChange([]))}
      {options.map(o => chip(o, values.includes(o), () => onChange(values.includes(o) ? values.filter(v => v !== o) : [...values, o])))}
    </div>
  );
};

// Plain text input (proportional font — for names, emails)
export const TextInput = ({ label, value, onChange, type = "text", placeholder, autoComplete, autoFocus, inputMode, onEnter, required, error, hint, maxLength, ariaLabel }) => {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <Eyebrow color={focus ? V.accent : V.text3} style={{ transition: "color .15s" }}>{label}{required && <span style={{ color: V.accent }}> *</span>}</Eyebrow>
      </div>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        autoComplete={autoComplete} autoFocus={autoFocus} inputMode={inputMode} maxLength={maxLength} aria-label={ariaLabel || label}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        onKeyDown={e => { if (e.key === "Enter" && onEnter) onEnter(); }}
        style={{
          width: "100%", padding: "13px 14px", background: V.inputBg, borderRadius: 12, color: V.text, fontSize: 16,
          fontFamily: V.font, outline: "none", boxSizing: "border-box", WebkitAppearance: "none", minHeight: 48,
          border: `1px solid ${error ? V.danger : focus ? `${V.accent}80` : V.cardBorder}`,
          boxShadow: focus ? `0 0 0 3px ${error ? V.danger : V.accent}1f` : "none", transition: "border-color .15s, box-shadow .15s",
        }} />
      {(error || hint) && <div style={{ fontSize: 11, marginTop: 5, color: error ? V.danger : V.text3 }}>{error || hint}</div>}
    </div>
  );
};

// Native select styled to match inputs, with a chevron
export const SelectField = ({ label, value, onChange, options, placeholder = "Select…", required }) => {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <Eyebrow color={focus ? V.accent : V.text3} style={{ marginBottom: 6, transition: "color .15s" }}>
        {label}{required && <span style={{ color: V.accent }}> *</span>}
      </Eyebrow>}
      <div style={{ position: "relative" }}>
        <select value={value} onChange={e => onChange(e.target.value)} aria-label={label}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          style={{
            width: "100%", padding: "13px 40px 13px 14px", background: V.inputBg, borderRadius: 12, fontSize: 16,
            color: value ? V.text : V.text3, fontFamily: V.font, outline: "none", minHeight: 48, cursor: "pointer",
            WebkitAppearance: "none", MozAppearance: "none", appearance: "none",
            border: `1px solid ${focus ? `${V.accent}80` : V.cardBorder}`,
            boxShadow: focus ? `0 0 0 3px ${V.accent}1f` : "none", transition: "border-color .15s, box-shadow .15s",
          }}>
          <option value="" style={{ background: V.bg, color: V.text }}>{placeholder}</option>
          {options.map(o => <option key={o} value={o} style={{ background: V.bg, color: V.text }}>{o}</option>)}
        </select>
        <span aria-hidden="true" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex" }}>
          {Icons.chevDown({ size: 16, color: focus ? V.accent : V.text3 })}
        </span>
      </div>
    </div>
  );
};

// Six-cell code / PIN entry. A real <input> sits invisibly over the cells so
// Landing feature tile
export const FeatureTile = ({ icon, color, title, desc }) => (
  <div style={{
    display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 14px", borderRadius: 16,
    background: V.cardGrad, border: `1px solid ${V.cardBorder}`, boxShadow: V.shadow,
  }}>
    <IconTile icon={icon} color={color} size={38} />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: V.text }}>{title}</div>
      <div style={{ fontSize: 12, color: V.text3, marginTop: 2, lineHeight: 1.45 }}>{desc}</div>
    </div>
  </div>
);

// 7-day schedule strip — Mon → Sun, coloured by session type
const dayColor = (t) => {
  const s = (t || "").toLowerCase();
  if (s === "rest") return null;
  if (/pull|back/.test(s)) return V.accent2;
  if (/leg|lower/.test(s)) return V.purple;
  if (/shoulder|arm|cardio/.test(s)) return V.warn;
  return V.accent; // push / chest / upper / full body
};
export const WeekStrip = ({ days, legend }) => {
  const order = [1, 2, 3, 4, 5, 6, 0];
  // Unique non-rest session types in week order, for the coloured legend
  const types = order.map(d => days[d] || "Rest").filter((t, i, a) => t !== "Rest" && a.indexOf(t) === i);
  return (
    <div style={{ marginTop: 4 }}>
      {legend && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 8px", marginBottom: 8 }}>
          {types.map(t => (
            <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: V.text3 }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, background: dayColor(t) }} />{t}
            </span>
          ))}
          {types.length === 0 && <span style={{ fontSize: 11, color: V.text3 }}>{legend}</span>}
        </div>
      )}
      <div aria-hidden="true" style={{ display: "flex", gap: 4 }}>
        {order.map((dow, i) => {
          const type = days[dow] || "Rest";
          const c = dayColor(type);
          return (
            <div key={dow} title={type} style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <div style={{ height: 14, borderRadius: 5, background: c ? `${c}cc` : track(), border: `1px solid ${c ? `${c}55` : "transparent"}` }} />
              <div style={{ fontSize: 9, color: c ? V.text2 : V.text3, marginTop: 3, fontWeight: 700 }}>{"MTWTFSS"[i]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Decorative in-app preview shown on the landing page ───
const Bars = ({ vals, color }) => (
  <svg viewBox="0 0 140 40" width="100%" height="40" aria-hidden="true" preserveAspectRatio="none">
    {vals.map((v, i) => (
      <rect key={i} x={i * 20 + 3} y={40 - v * 0.4} width={14} height={v * 0.4} rx={3}
        fill={i === vals.length - 1 ? color : `${color}55`} />
    ))}
  </svg>
);
const Ring = ({ pct, color, size = 54, label, sub }) => {
  const r = (size - 8) / 2, c = 2 * Math.PI * r;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track()} strokeWidth={6} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={6} fill="none" strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x="50%" y="50%" dy="4" textAnchor="middle" fontSize="12" fontWeight="700" fontFamily={V.mono} fill={V.text}>{label}</text>
      </svg>
      <span style={{ fontSize: 9, color: V.text3, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>{sub}</span>
    </div>
  );
};
export const AppPreview = ({ scale = 1 }) => (
  <div aria-hidden="true" style={{ position: "relative", width: "100%", maxWidth: 360, margin: "0 auto", transform: `scale(${scale})`, transformOrigin: "top center" }}>
    <div style={{ position: "absolute", inset: -30, background: `radial-gradient(ellipse at 50% 30%, ${V.accent}22, transparent 65%)`, filter: "blur(20px)", pointerEvents: "none" }} />
    <div style={{
      position: "relative", borderRadius: 26, padding: 16, background: V.mode === "dark" ? "rgba(14,14,22,0.9)" : "rgba(255,255,255,0.9)",
      border: `1px solid ${V.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.08)"}`, boxShadow: V.shadowLg, backdropFilter: "blur(12px)",
      animation: "obFloat 7s ease-in-out infinite",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: V.text3, fontWeight: 600 }}>Good evening</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: V.text, letterSpacing: "-0.01em" }}>Ready to lift, Alex?</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 20, background: `${V.warn}18`, border: `1px solid ${V.warn}30` }}>
          {Icons.flame({ size: 12, color: V.warn })}
          <span style={{ fontSize: 11, fontWeight: 800, color: V.warn, fontFamily: V.mono }}>12</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div style={{ padding: 12, borderRadius: 14, background: V.cardGlow, border: `1px solid ${V.accent}26` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            {Icons.trophy({ size: 12, color: V.accent })}
            <span style={{ fontSize: 9, fontWeight: 700, color: V.text3, textTransform: "uppercase", letterSpacing: ".06em" }}>New PR</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, fontFamily: V.mono, color: V.text, lineHeight: 1 }}>315<span style={{ fontSize: 10, color: V.text3, marginLeft: 3 }}>lb</span></div>
          <div style={{ fontSize: 10, color: V.text2, marginTop: 4 }}>Bench press · +10</div>
        </div>
        <div style={{ padding: 12, borderRadius: 14, background: V.cardGrad, border: `1px solid ${V.cardBorder}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            {Icons.chart({ size: 12, color: V.accent2 })}
            <span style={{ fontSize: 9, fontWeight: 700, color: V.text3, textTransform: "uppercase", letterSpacing: ".06em" }}>Volume</span>
          </div>
          <Bars vals={[52, 66, 48, 80, 62, 90, 100]} color={V.accent2} />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-around", padding: "12px 6px", borderRadius: 14, background: V.cardGrad, border: `1px solid ${V.cardBorder}` }}>
        <Ring pct={0.82} color={V.accent} label="162" sub="Protein" />
        <Ring pct={0.64} color={V.accent2} label="1.9k" sub="Calories" />
        <Ring pct={0.45} color={V.purple} label="3/5" sub="Sessions" />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, padding: "10px 12px", borderRadius: 14, background: V.cardGrad, border: `1px solid ${V.cardBorder}` }}>
        <IconTile icon={Icons.dumbbell} color={V.accent} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: V.text }}>Today · Push</div>
          <div style={{ fontSize: 10, color: V.text3 }}>Bench · OHP · Dips · Lateral raise</div>
        </div>
        <div style={{ width: 28, height: 28, borderRadius: 14, background: gradient(), display: "flex", alignItems: "center", justifyContent: "center" }}>
          {Icons.play({ size: 12, color: V.mode === "dark" ? V.bg : "#fff" })}
        </div>
      </div>
    </div>
  </div>
);

export const InstallGuide = ({ isIOS, isAndroid, onClose }) => ReactDOM.createPortal(
  <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.7)", backdropFilter: "blur(6px)", animation: "fadeIn .2s ease-out" }} onClick={onClose} />
    <div role="dialog" aria-modal="true" aria-label={`Install ${appName()}`} style={{
      position: "relative", background: V.sheetBg, borderRadius: 22, padding: 24, maxWidth: 380, width: "100%",
      border: `1px solid ${V.cardBorder}`, boxShadow: V.shadowLg, maxHeight: "85vh", overflowY: "auto",
      animation: "popIn .25s cubic-bezier(.2,.8,.3,1) both",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <LogoMark size={44} />
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: V.text }}>Install {appName()}</div>
          <div style={{ fontSize: 12, color: V.text3, marginTop: 2 }}>Works offline, sends reminders, feels native</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {isIOS ? (<>
          <NumberedStep n={1} title="Tap the Share button" desc="The square icon with an arrow at the bottom of Safari"
            chip={{ label: "Share", icon: Icons.upload({ size: 14, color: V.accent }) }} />
          <NumberedStep n={2} title='Scroll down and tap "Add to Home Screen"' desc="You may need to scroll the share menu to find it"
            chip={{ label: "Add to Home Screen", icon: Icons.plus({ size: 14, color: V.accent }) }} />
          <NumberedStep n={3} title='Tap "Add" in the top right' desc={`${appName()} appears on your home screen like a real app`} />
          <NumberedStep n={4} title="Open it from your home screen" desc="Push notifications only work when launched from the icon, not from Safari" />
          <Callout icon={Icons.zap} color={V.warn} title="iPhone tip">
            Use Safari (not Chrome) and open {appName()} from the home screen icon for full features, including push notifications.
          </Callout>
        </>) : isAndroid ? (<>
          <NumberedStep n={1} title="Tap the menu (⋮) in Chrome" desc="Three dots in the top right corner" />
          <NumberedStep n={2} title='Tap "Add to Home screen"' desc='Then tap "Add" to confirm' />
          <NumberedStep n={3} title="Open it from your home screen" desc="Full app experience with notifications and offline support" />
        </>) : (<>
          <NumberedStep n={1} title="Look for the install icon in the address bar" desc={`In Chrome or Edge — or open the browser menu and choose "Install ${appName()}".`} />
          <NumberedStep n={2} title="Open it from your dock or start menu" desc="The app runs in its own window with full offline support." />
        </>)}
      </div>
      <Btn full onClick={onClose} s={{ marginTop: 20 }}>Got it</Btn>
    </div>
  </div>,
  document.body,
);
